import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonComponent, InfoBannerComponent, ModalComponent } from '@catch/general-ui';
import { ToastsService } from '@catch/services';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { PlatformAuthService } from '../../../../services/platform-auth.service';

@Component({
  selector: 'app-platform-settings-delete',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    ButtonComponent,
    InfoBannerComponent,
    ModalComponent,
  ],
  templateUrl: './platform-settings-delete.component.html',
  styleUrl: './platform-settings-delete.component.scss',
})
export class PlatformSettingsDeleteComponent {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(PlatformAuthService);
  private readonly toastsService = inject(ToastsService);
  private readonly transloco = inject(TranslocoService);
  private readonly router = inject(Router);

  readonly deleteModal = viewChild<ModalComponent>('deleteModal');
  readonly submitAttempt = signal(false);
  readonly deleting = signal(false);

  readonly confirmForm = this.fb.group({
    confirmValue: this.fb.control('', [Validators.required]),
  });

  /**
   * Opens the account-delete confirmation modal.
   */
  openModal(): void {
    this.submitAttempt.set(false);
    this.confirmForm.reset();
    this.deleteModal()?.open();
  }

  /**
   * Deletes the account after the confirmation word matches.
   */
  confirmDelete(): void {
    this.submitAttempt.set(true);
    if (this.confirmForm.invalid) {
      return;
    }
    const expected = this.confirmationWord();
    const typed = (this.confirmForm.controls.confirmValue.value ?? '').trim();
    if (typed !== expected) {
      this.confirmForm.controls.confirmValue.setErrors({ incorrect: true });
      return;
    }
    this.deleting.set(true);
    this.auth.deleteAccount().subscribe({
      next: () => {
        this.deleting.set(false);
        this.deleteModal()?.close();
        this.toastsService.success('jobboard.platform.settings.delete.deleted-toast');
        this.router.navigate(['/']);
      },
      error: () => {
        this.deleting.set(false);
        this.toastsService.warning('jobboard.platform.settings.delete.error');
      },
    });
  }

  /**
   * Confirmation word the applicant must type.
   * @returns Language-specific confirmation word
   */
  confirmationWord(): string {
    return this.transloco.translate('jobboard.platform.settings.delete.confirm-word');
  }
}
